import React from 'react';
import { LockKeyhole } from 'lucide-react';
import { cn } from '../../utils/cn';
import { ChartDataPoint } from '../../types/game';
import { StockCategoryFilter } from '../../types/stockCategories';
import { getCategoryColor } from '../../config/categoryConfig';

interface ChartSummaryBannerProps {
  latestPrice: number | null;
  chartData: ChartDataPoint[];
  selectedPeriod: string;
  lineColor: string;
  showPrice?: boolean;
  category?: StockCategoryFilter;
}

const CURRENCY_FORMAT = new Intl.NumberFormat('en-US', {
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

const PERCENT_FORMAT = new Intl.NumberFormat('en-US', {
  minimumFractionDigits: 1,
  maximumFractionDigits: 1,
});

const formatPercentage = (value: number): string => {
  if (value > 0) {
    return `+${PERCENT_FORMAT.format(value)}%`;
  }

  if (value < 0) {
    return `-${PERCENT_FORMAT.format(Math.abs(value))}%`;
  }

  return '0.0%';
};

const getPerformanceTextColorClass = (lineColor: string): string => {
  if (lineColor === '#22c55e') return 'text-green-600';
  if (lineColor === '#ef4444') return 'text-red-600';
  return '';
};

const ChartSummaryBannerComponent: React.FC<ChartSummaryBannerProps> = ({
  latestPrice,
  chartData,
  selectedPeriod,
  lineColor,
  showPrice = true,
  category,
}) => {
  if (latestPrice === null) {
    return null;
  }

  const hasEnoughData = chartData.length >= 2 && chartData[0].price !== 0;

  const performancePercentage = hasEnoughData
    ? ((chartData[chartData.length - 1].price - chartData[0].price) / chartData[0].price) * 100
    : null;

  const periodLabel = selectedPeriod.charAt(0).toUpperCase() + selectedPeriod.slice(1);

  return (
    <div className="flex items-center gap-1 sm:gap-2 mt-1 text-[0.625rem] sm:text-[clamp(0.75rem,1.4vw,0.875rem)] text-muted-foreground">
      {/* Price */}
      <span className="flex items-center gap-1">
        <span className="font-medium">Last EOD Price:</span>
        {showPrice ? (
          <span className="font-mono tabular-nums text-foreground">${CURRENCY_FORMAT.format(latestPrice)}</span>
        ) : (
          <span className="flex items-center gap-1" style={{ color: category ? getCategoryColor(category) : '#6B7280' }}>
            <LockKeyhole className="w-3 h-3 shrink-0" />
            <span className="font-extrabold text-xs">Hint 2</span>
          </span>
        )}
      </span>

      {/* Separator & Performance */}
      {performancePercentage !== null && (
        <>
          <span className="text-muted-foreground/50">|</span>
          <span className="flex items-center gap-1">
            <span className="font-medium">{periodLabel} Performance:</span>
            <span className={cn('font-mono tabular-nums', getPerformanceTextColorClass(lineColor))}>
              {formatPercentage(performancePercentage)}
            </span>
          </span>
        </>
      )}
    </div>
  );
};

export const ChartSummaryBanner = React.memo(ChartSummaryBannerComponent);

export default ChartSummaryBanner;
